import type { ScopeRef } from "@enduring-systems/entitlements";
import { useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/common/ui/alert-dialog";

import { scopeKindLabel } from "../scope";
import {
  describeOfferingChange,
  type OfferingAction,
  type OfferingChangePreview,
} from "./offering-mutation";
import type { OfferingCard } from "./offering-state";

const ACTION_LABEL: Readonly<Record<OfferingAction, string>> = {
  apply: "Apply",
  retract: "Retract",
};

const IN_FLIGHT_LABEL: Readonly<Record<OfferingAction, string>> = {
  apply: "Applying…",
  retract: "Retracting…",
};

export interface OfferingActionDialogProps {
  readonly action: OfferingAction;
  readonly card: OfferingCard;
  readonly scope: ScopeRef;
  readonly busy: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  /**
   * An alternative framing for the same write (ENR-1949's approve-and-apply).
   *
   * Only the words change. The scope line, the blast radius, the member values
   * and the derived-rows note are all still computed from the same card and
   * scope, because the point of reusing this dialog is that one write reads
   * identically wherever an operator started from. A caller that needed
   * different VALUES would need a different dialog, not these props.
   */
  readonly heading?: string;
  readonly confirmLabel?: string;
  readonly inFlightLabel?: string;
  /** Context the other surface has and this one does not, such as who asked. */
  readonly context?: React.ReactNode;
  /**
   * The change this write would make, when it is not the plain apply this
   * dialog derives by default.
   *
   * ENR-1947's quota increase is a DIFFERENT write: the service grows each
   * quota lever on top of what the scope holds rather than setting it to the
   * authored figure, so the apply's "set to" lines would state numbers the
   * write never produces. The caller passes a preview built from the SAME card
   * by `describeOfferingIncrement`, so this stays one confirmation surface
   * reading one scope-state answer rather than two dialogs.
   */
  readonly preview?: OfferingChangePreview;
}

/**
 * The confirmation between pressing Apply or Retract and the write happening.
 *
 * It states what will change and where, in the operator's words rather than the
 * system's: the member capability values being written or removed, the scope,
 * and how far the write reaches. Every line comes from the same scope-state read
 * the card behind it derives its state from.
 *
 * Closing only closes. Radix reports Cancel, Escape and an outside click through
 * one `onOpenChange`, so running the action from there would perform it when the
 * operator declined; only the action control calls `onConfirm`.
 */
export function OfferingActionDialog({
  action,
  card,
  scope,
  busy,
  onConfirm,
  onCancel,
  heading,
  confirmLabel,
  inFlightLabel,
  context,
  preview,
}: OfferingActionDialogProps): React.ReactElement {
  /**
   * Whether this dialog's own action has been sent.
   *
   * Local rather than read from `busy`, because `busy` is the parent's state and
   * does not commit until after the click that started the write. Dismissing in
   * that window would take the confirmation down over a write already in flight.
   * A flag set synchronously in the click handler cannot be raced that way.
   */
  const [sent, setSent] = useState(false);
  const settling = busy || sent;
  const change = preview ?? describeOfferingChange(action, card, scope);
  const scopeText =
    scope.kind === "platform"
      ? scopeKindLabel("platform")
      : `${scopeKindLabel(scope.kind)} ${scope.id}`;

  return (
    <AlertDialog
      open
      onOpenChange={(open) => {
        // Dismissal only, and not while a write is in flight: a response landing
        // after the dialog closed and reopened would report one offering's
        // outcome under another's name.
        if (!open && !settling) onCancel();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="offering-action-title">
            {heading ?? `${ACTION_LABEL[action]} ${card.offering.title}?`}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              {context}
              <p data-testid="offering-action-scope">
                {action === "apply"
                  ? `Target: ${scopeText}, at v${card.offering.version}.`
                  : /* No version, because the request carries none: the store
                       removes EVERY applied version of the bundle. Naming the
                       catalog's version here read as a promise to remove only
                       that one, and on a drifted scope it named a version that
                       was not even the one applied. */
                    `Target: ${scopeText}. This removes every version of ${card.offering.name} applied here.`}
              </p>
              <p data-testid="offering-action-blast-radius">{change.blastRadiusText}</p>

              {/* Lines render whenever there ARE any, refused or not: a
                  per-member refusal ("nothing granted here to increase") puts
                  its explanation on a line and sets no `refusalReason`, so
                  hiding lines on refusal left the paragraph below pointing at
                  a reason that was never shown. Only the EMPTY-state sentence
                  is suppressed on a refusal, because an offering-level refusal
                  carries `refusalReason` and "names no capabilities" would be
                  a different and untrue claim. */}
              {change.lines.length > 0 ? (
                <ul className="space-y-1" data-testid="offering-action-lines">
                  {change.lines.map((line) => (
                    <li
                      key={line.capabilityId}
                      data-testid={`offering-action-line-${line.capabilityId}`}
                    >
                      <code className="text-xs">{line.capabilityId}</code>: {line.detail}
                    </li>
                  ))}
                </ul>
              ) : change.refused === true ? null : (
                <p data-testid="offering-action-empty">
                  {action === "retract"
                    ? "This offering has no rows of its own at this scope, so there is nothing to remove."
                    : "This offering names no capabilities, so applying it would write nothing."}
                </p>
              )}

              {change.refused === true && (
                <p data-testid="offering-action-refused">
                  {change.refusalReason ??
                    "The service will refuse this write for the reason above, so it is not offered here."}{" "}
                  Nothing would change.
                </p>
              )}

              {change.survivingOverrides.length > 0 && (
                <ul className="space-y-1" data-testid="offering-action-surviving">
                  {change.survivingOverrides.map((line) => (
                    <li key={line.capabilityId}>
                      <code className="text-xs">{line.capabilityId}</code>: {line.detail}
                    </li>
                  ))}
                </ul>
              )}

              {action === "apply" && (
                /* The catalog carries the levers a human authored; publication
                   derives a paired `.enabled` boolean from each quota lever, and
                   that pair is not in the catalog read. Listing the levers as if
                   they were the whole row set would understate what the apply
                   writes, so the list says what it is. */
                <p data-testid="offering-action-derived-note">
                  These are the levers the catalog authors. Publication may derive further rows from
                  them, and the raw grants tab shows every row once this lands.
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="offering-action-cancel" disabled={settling}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="offering-action-confirm"
            disabled={settling || change.refused === true}
            onClick={(event) => {
              // The dialog is unmounted by the caller once the write settles, so
              // Radix must not close it first and lose the in-flight label.
              event.preventDefault();
              setSent(true);
              onConfirm();
            }}
          >
            {settling
              ? (inFlightLabel ?? IN_FLIGHT_LABEL[action])
              : (confirmLabel ?? ACTION_LABEL[action])}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
